import { FlightsSearchFormSubmitInterface } from '@smartlinks/react-design-system/dist/src/lib/flights/shared/interfaces/flights-search-form.interface';
import { FlightsSearchFormUtil } from '@smartlinks/react-design-system/dist/src/lib/flights/shared/utils/flights-search-form.util';
import { FlightsSearchAirportsInterface, FlightsSearchAirlinesInterface } from '../interfaces/flights-airports.interface';
import { ResponseCommons } from '../../shared/interfaces/commons.interface';
import { CommonNumberMagicEnum } from '../enums/common-number-magic.enum';
import { LocalStorageHistoriesNames } from '../enums/local-storage.enum';
import { ResponseHttp } from '../interfaces/response-http.interface';
import { endPoints } from '../end-points/end-points';
import { CommonsService } from './commons.service';
import { Airport } from '../interfaces/airport';

export class FlightsService extends CommonsService {
	private readonly allowedSelectedDepartureAirport: FlightsSearchAirportsInterface[] = [];
	private readonly allowedSelectedArrivalAirport: FlightsSearchAirportsInterface[] = [];

	constructor(token: string) {
		super(token, LocalStorageHistoriesNames.flightsSearch);
	}

	public async getAirports(): Promise<FlightsSearchAirportsInterface[] | null> {
		const url: string = `${this.managementApi}/${endPoints.airports}`;
		const airportsResponse: ResponseHttp<FlightsSearchAirportsInterface[]> = await this.httpCall.get(url, this.header);
		if (airportsResponse.response != null) {
			return airportsResponse.response;
		}

		return null;
	}

	public async getAirportsByWords(value: string): Promise<FlightsSearchAirportsInterface[] | null> {
		const url: string = `${this.managementApi}/${endPoints.airportsByWords(value)}`;
		const airportsResponse: ResponseHttp<FlightsSearchAirportsInterface[]> = await this.httpCall.get(url, this.header);
		if (airportsResponse.response != null) {
			return airportsResponse.response;
		}

		return null;
	}

	public async getAirlines(): Promise<FlightsSearchAirlinesInterface[] | null> {
		const url: string = `${this.managementApi}/${endPoints.airlines}`;
		const airlinesResponse: ResponseHttp<FlightsSearchAirlinesInterface[]> = await this.httpCall.get(url, this.header);
		if (airlinesResponse.response != null) {
			return airlinesResponse.response;
		}

		return null;
	}

	public getFlightsSearchFormHistory(traductions?: (label: string) => string): FlightsSearchFormSubmitInterface | null {
		const tempHistory: ResponseCommons<FlightsSearchFormSubmitInterface> = this.getHistory<FlightsSearchFormSubmitInterface>(
			FlightsSearchFormUtil.historyHandle,
			traductions,
		);

		return tempHistory?.response || null;
	}

	public async getSelectedAirports(agencyId: string, search: string, isDeparture: boolean): Promise<FlightsSearchAirportsInterface[] | null> {
		const options: { endpoint: (agencyId: string) => string; cache: FlightsSearchAirportsInterface[] } = {
			endpoint: isDeparture ? endPoints.selectedDepartureAirports : endPoints.selectedArrivalAirports,
			cache: isDeparture ? this.allowedSelectedDepartureAirport : this.allowedSelectedArrivalAirport,
		};

		const { endpoint, cache } = options;
		if (cache.length) return this.airportSearch(cache, search);

		const url: string = `${this.managementApi}/${endpoint(agencyId)}`;
		const airportsResponse: ResponseHttp<Airport[]> = await this.httpCall.get(url, this.header);
		if (airportsResponse.response != null) {
			const mappedAirports: Airport[] =
				airportsResponse.response.map((airport: Airport) => ({
					id: airport.id,
					code: airport.code,
					description: airport.value,
					name: airport.name,
					country: airport.country,
					countryCode: airport.countryCode,
					latitude: airport.latitude,
					longitude: airport.longitude,
					value: airport.value,
				})) || [];

			cache.splice(CommonNumberMagicEnum.ZERO, cache.length, ...mappedAirports);

			return this.airportSearch(cache, search);
		}

		return null;
	}

	public airportSearch(airport: FlightsSearchAirportsInterface[], search: string): FlightsSearchAirportsInterface[] {
		return airport.filter((airport: FlightsSearchAirportsInterface) => {
			const searchLower: string = search.toLowerCase();
			const empty: string = '';
			return (
				(airport.name?.toLowerCase() ?? empty).includes(searchLower) ||
				(airport.code?.toLowerCase() ?? empty).includes(searchLower) ||
				(airport.value?.toLowerCase() ?? empty).includes(searchLower)
			);
		});
	}
}
